import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { client } from '../../api/client';
import { Home, Users, BookOpen, LogOut, Shield, Star, UserCog, Menu, X, Calendar, Clock, Monitor } from 'lucide-react';
import { GlobalFooter } from '../../components/GlobalFooter';

// Tab definitions with required admin level
const ALL_TABS = [
  { name: 'Community Feed', path: '/admin', icon: Home, minLevel: 1 },
  { name: 'Clients', path: '/admin/clients', icon: Users, minLevel: 3 },
  { name: 'Upcoming Sessions', path: '/admin/upcoming', icon: Calendar, minLevel: 1 },
  { name: 'Past Sessions', path: '/admin/past', icon: Clock, minLevel: 1 },
  { name: 'Reviews', path: '/admin/reviews', icon: Star, minLevel: 4 },
  { name: 'Device Access', path: '/admin/devices', icon: Monitor, minLevel: 5 },
];

// Level-based tab filtering (FIX #6)
function getTabsForLevel(adminLevel: number, isSuperadmin: boolean): typeof ALL_TABS {
  if (isSuperadmin) {
    return [...ALL_TABS, { name: 'Manage Admins', path: '/admin/admins', icon: UserCog, minLevel: 99 }];
  }
  return ALL_TABS.filter(tab => adminLevel >= tab.minLevel);
}

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, reset } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminLevel, setAdminLevel] = useState<number>(user?.adminLevel || 1);

  const isSuperadmin = user?.role === 'superadmin';

  // Fetch fresh admin level from profile
  useEffect(() => {
    async function fetchLevel() {
      try {
        const res = await client.api.fetch('/api/profile');
        const data = await res.json();
        if (data?.data?.adminLevel) {
          setAdminLevel(data.data.adminLevel);
        }
      } catch {}
    }
    if (!isSuperadmin) {
      fetchLevel();
    }
  }, [isSuperadmin]);

  const handleLogout = async () => {
    await client.auth.signOut();
    reset();
    navigate('/login');
  };

  // FIX #6: Filter tabs based on admin level
  const tabs = getTabsForLevel(isSuperadmin ? 99 : adminLevel, isSuperadmin);

  // Check if current path matches tab
  const isActiveTab = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Admin Hub Header */}
      <header className="bg-gray-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/admin" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-nhs-blue" />
              <span className="text-xl font-bold">Admin Hub</span>
            </Link>

            {/* User info & logout */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-nhs-blue flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="text-sm">
                  <p className="font-medium">{user?.name || 'Admin'}</p>
                  <p className="text-gray-400 text-xs">
                    {isSuperadmin ? 'Super Admin' : `Admin Level ${adminLevel}`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1 text-gray-300 hover:text-white text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Mobile tab selector */}
          <div className="sm:hidden">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-between py-3 text-gray-700"
            >
              <span className="font-medium">
                {tabs.find(t => isActiveTab(t.path))?.name || 'Select Tab'}
              </span>
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Desktop tabs */}
          <nav className="hidden sm:flex space-x-1 overflow-x-auto py-1">
            {tabs.map((tab) => (
              <Link
                key={tab.name}
                to={tab.path}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  isActiveTab(tab.path)
                    ? 'bg-nhs-blue text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.name}
              </Link>
            ))}
          </nav>

          {/* Mobile tabs dropdown */}
          {sidebarOpen && (
            <nav className="sm:hidden py-2 border-t border-gray-200">
              {tabs.map((tab) => (
                <Link
                  key={tab.name}
                  to={tab.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium ${
                    isActiveTab(tab.path)
                      ? 'bg-nhs-blue text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.name}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-6 w-full">
        {children}
      </main>

      <GlobalFooter />
    </div>
  );
}
